import { getApiUrl } from "../api";
import { useEffect, useMemo, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { getCustomerRequestNumber } from "../utils/requestReferences";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";
import SmsDonationOptions from "../component/SmsDonationOptions";

const projectTrustItems = [
  { icon: "fa-solid fa-shield-halved", title: "Secure Donation", text: "Your donation is safe and secure" },
  { icon: "fa-solid fa-award", title: "Trusted & Certified", text: "Registered with official authorities" },
  { icon: "fa-regular fa-heart", title: "100% Donation Policy", text: "Your donation reaches those in need" },
  { icon: "fa-solid fa-people-group", title: "Make an Impact", text: "Change a life and bring hope" },
];

const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};


const parseAmount = (value) => {
  const amount = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const formatAmount = (value) => parseAmount(value).toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const mapCaseDetail = (item, isProjectPage = false, language = "en") => {
  if (!item) return {};
  const amount = parseAmount(item.amount);
  const collectedAmount = parseAmount(item.collectedamount || item.collectedAmount);
  const remainingAmount = Math.max(0, amount - collectedAmount);
  return {
    id: item.id || "",
    catid: item.catid || "0",
    projectid: item.id || "0",
    title: language === "ar" ? (item.title_ar || item.title_en || item.title || "") : (item.title_en || item.title || ""),
    caseNo: getCustomerRequestNumber(item),
    image: getCloudflareImageUrl(item.ImagePath || item.image, "w=900,sharpen=1"),
    description: stripHtml(language === "ar" ? (item.description_ar || item.shortdescription_ar || item.description_en || item.shortdescription_en || item.description) : (item.description_en || item.shortdescription_en || item.description)),
    amount, collectedAmount, remainingAmount,
    categoryTitle: language === "ar" ? (item.category_title_ar || item.category_title_en || item.categoryTitle || "") : (item.category_title_en || item.categoryTitle || ""),
  };
};

export default function CriticalCaseDetails({ type = "Case", language = "en" }) {
  const interactions = useHomeInteractions();
  const isArabic = language === "ar";
  const isProjectPage = type === "Project";
  const pageTitle = isProjectPage ? (isArabic ? "مشاريعنا" : "Our Projects") : (isArabic ? "الحالات الحرجة" : "Critical Cases");
  const [caseDetail, setCaseDetail] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isShareOpen, setIsShareOpen] = useState(false);
  const text = isArabic ? {
    home: "الرئيسية", loadingProject: "جارٍ تحميل المشروع...", loadingCase: "جارٍ تحميل الحالة...", amount: "أدخل المبلغ (درهم)", donate: "تبرع الآن", secure: "تبرع آمن", otherWays: "طرق أخرى للتبرع", code: "الرمز", caseNo: "الحالة رقم", progress: "تقدم التبرع", remaining: "المبلغ المتبقي", collected: "المبلغ المحصل", choose: "اختر مبلغ التبرع", quick: "أو اختر مبلغًا سريعًا", secureNote: "تبرعك آمن وسيساعد مباشرةً المحتاجين. شكرًا لصنعك فرقًا.", projectSecure: "تبرعك آمن ويُحدث فرقًا حقيقيًا.", currency: "درهم", benefits: ["توفير خدمات الرعاية الصحية الأساسية للمحتاجين", "تغطية تكاليف العلاج والأدوية", "تسهيل الإجراءات الجراحية اللازمة", "ضمان المتابعة والرعاية الطبية المستمرة"]
  } : {
    home: "home", loadingProject: "Loading project...", loadingCase: "Loading critical case...", amount: "Enter Amount (AED)", donate: "Donate Now", secure: "Secure Donation", otherWays: "Other Ways to Donate", code: "Code", caseNo: "Case no", progress: "Donation Progress", remaining: "Remaining amount", collected: "Collected amount", choose: "Choose Donation Amount", quick: "Or choose a quick amount", secureNote: "Your donation is secure and will directly help those in need. Thank you for making a difference.", projectSecure: "Your donation is secure and makes a real difference.", currency: "AED", benefits: ["Provide essential healthcare services to those in need", "Cover treatment and medication costs", "Facilitate necessary surgical procedures", "Ensure continuous medical follow-up and care"]
  };
  const trustItems = isArabic ? [
    { icon: "fa-solid fa-shield-halved", title: "تبرع آمن", text: "تبرعك آمن ومحمي" },
    { icon: "fa-solid fa-award", title: "موثوق ومعتمد", text: "مسجل لدى الجهات الرسمية" },
    { icon: "fa-regular fa-heart", title: "سياسة التبرع بنسبة 100٪", text: "يصل تبرعك إلى المحتاجين" },
    { icon: "fa-solid fa-people-group", title: "اصنع أثرًا", text: "غيّر حياةً وامنح الأمل" },
  ] : projectTrustItems;
  const caseId = useMemo(() => new URLSearchParams(window.location.search).get("id") || "", []);
  const percent = caseDetail.amount
    ? Math.max(0, Math.min(100, Math.round((caseDetail.collectedAmount / caseDetail.amount) * 100)))
    : 0;
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const encodedShareUrl = encodeURIComponent(shareUrl);
  const encodedShareTitle = encodeURIComponent(caseDetail.title || pageTitle);
  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedShareUrl}&text=${encodedShareTitle}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodedShareTitle}%20${encodedShareUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}`,
  };

  useEffect(() => {
    document.title = `${caseDetail.title || pageTitle} | Tarahum Charity Foundation`;
  }, [caseDetail.title, pageTitle]);

  useEffect(() => {
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.body.classList.remove("arabic-layout-page");
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
    };
  }, [isArabic]);

  useEffect(() => {
    const controller = new AbortController();

    const loadCaseDetail = async () => {
      try {
        setIsLoading(true);
        setLoadError("");

        if (!caseId) {
          setCaseDetail({});
          return;
        }

        const headers = { Accept: "application/json" };
        const token = getJwtToken();
        if (token) headers.Authorization = "Bearer " + token;

        const response = await fetch(
          getApiUrl(`/api/GetDonationProjectdetails.ashx?id=${encodeURIComponent(caseId)}&typ=${type}`),
          {
            method: "GET",
            headers,
            credentials: "include",
            signal: controller.signal,
          }
        );

        if (!response.ok) throw new Error("Donation details API failed with status " + response.status);

        const result = await response.json();
        const detail = Array.isArray(result?.data) && result.data.length ? mapCaseDetail(result.data[0], isProjectPage, language) : {};
        setCaseDetail(detail);
      } catch (error) {
        if (error.name !== "AbortError") {
          setLoadError(`Unable to load ${pageTitle.toLowerCase()} details.`);
          setCaseDetail({});
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadCaseDetail();
    return () => controller.abort();
  }, [caseId, isProjectPage, language, pageTitle, type]);

  if (isProjectPage) {
    return (
      <>
        <Preloader />
        {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}
        <section className="kid-d-wrp pb-0">
          <div className="kid-container">
            <div className="kid-hero">
              <div className="kid-badge">
                <i className="fa-solid fa-box-open"></i>
                {` ${pageTitle}`}
              </div>
              <h1>{pageTitle}</h1>
              <ul className="kid-breadcrumb">
                <li><Link to="/">{text.home}</Link></li>
                <li>{"/"}</li>
                <li>{pageTitle}</li>
              </ul>
            </div>
          </div>
        </section>

        <main className="project-detail-page">
          <div className="project-detail-accent"></div>
          <div className="container project-detail-container">
            {loadError ? <div className="donation-error">{loadError}</div> : null}
            <div className="project-detail-grid">
              <section className="project-visual-card">
                <div className="project-dot-pattern" aria-hidden="true"></div>
                <div
  className="project-line-pattern"
  aria-hidden="true"
  style={{
    backgroundImage: `url(${caseDetail.image})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center center",
  }}
></div>
           
                <button
                  type="button"
                  className="project-share-main"
                  aria-label="Share this project"
                  aria-expanded={isShareOpen}
                  onClick={() => setIsShareOpen((current) => !current)}
                >
                  <i className="fas fa-share-alt"></i>
                </button>
                <div className={`project-share-list ${isShareOpen ? "active" : ""}`}>
                  <a href={shareLinks.facebook} title="Facebook" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a href={shareLinks.twitter} title="Twitter" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-twitter"></i>
                  </a>
                  <a href={shareLinks.whatsapp} title="WhatsApp" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-whatsapp"></i>
                  </a>
                  <a href={shareLinks.linkedin} title="LinkedIn" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                </div>
              </section>

              <section className="project-donation-card">
                <div className="project-title-row">
                  <div className="project-title-icon">
                    <i className="fa-solid fa-heart-circle-check"></i>
                  </div>
                  <div>
                    <h1>{isLoading ? text.loadingProject : caseDetail.title}</h1>
                    <p>{caseDetail.description}</p>
                  </div>
                </div>

                <div className="project-divider"></div>

                <label className="project-amount-label" htmlFor="project-donation-amount">{text.amount}</label>
                <div className="project-donate-row">
                  <input id="project-donation-amount" type="number" defaultValue="100" min="1" />
                  <button
                    type="button"
                    className="openDonation"
                    data-donation-id={caseDetail.id}
                    data-catid={caseDetail.catid || 0}
                    data-subcatid="0"
                    data-projectid={caseDetail.projectid || caseDetail.id || 0}
                    data-donation-title={caseDetail.title}
                    data-donation-type="project"
                    data-donation-type-label="Projects"
                    data-case-no=""
                    data-category-title={caseDetail.categoryTitle || ""}
                    data-donation-amount="100"
                  >
                    <span><i className="fa-solid fa-hands-holding-child"></i></span>
                    {text.donate}
                  </button>
                </div>
                <div className="project-secure-note">
                  <i className="fa-solid fa-shield-heart"></i>
                  {text.projectSecure}
                </div>

                <SmsDonationOptions
                  className="project-sms-box"
                  language={language}
                  title={text.otherWays}
                  currency={text.currency}
                  codeLabel={text.code}
                  iconClass="fa-solid fa-hands-holding-circle"
                />
              </section>
            </div>

            <div className="project-trust-strip">
              {trustItems.map((item) => (
                <div className="project-trust-item" key={item.title}>
                  <span><i className={item.icon}></i></span>
                  <div>
                    <strong>{item.title}</strong>
                    <p>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>

        {isArabic ? <ArabicFooter /> : <Footer />}
        <FloatingWidgets {...interactions} />
        <HomeModals {...interactions} />
      </>
    );
  }

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}
      <main className="case-detail-modern">
        <div className="container case-detail-modern-container">
          {loadError ? <div className="donation-error">{loadError}</div> : null}
          <div className="case-detail-modern-grid">
            <section className="case-story-card">
              <div className="case-story-image">
                <img src={caseDetail.image} alt={caseDetail.title} />
                {caseDetail.caseNo?.trim() ? (
                  <div className="case-story-badge">
                    <i className="fa-solid fa-shield-heart"></i>
                    {text.caseNo} {caseDetail.caseNo}
                  </div>
                ) : null}
                <button
                  type="button"
                  className="case-story-share"
                  aria-label="Share this donation"
                  aria-expanded={isShareOpen}
                  onClick={() => setIsShareOpen((current) => !current)}
                >
                  <i className="fas fa-share-alt"></i>
                </button>
                <div className={`case-story-share-list ${isShareOpen ? "active" : ""}`}>
                  <a href={shareLinks.facebook} title="Facebook" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a href={shareLinks.twitter} title="Twitter" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-twitter"></i>
                  </a>
                  <a href={shareLinks.whatsapp} title="WhatsApp" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-whatsapp"></i>
                  </a>
                  <a href={shareLinks.linkedin} title="LinkedIn" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                </div>
              </div>

              <div className="case-story-body">
                <h1>{isLoading ? text.loadingCase : caseDetail.title}</h1>
                <p>{caseDetail.description}</p>
                <div className="case-story-divider"></div>
                <div className="case-benefits">
                  {[
                    ["fa-solid fa-hand-holding-heart", text.benefits[0]],
                    ["fa-solid fa-briefcase-medical", text.benefits[1]],
                    ["fa-solid fa-stethoscope", text.benefits[2]],
                    ["fa-solid fa-people-group", text.benefits[3]],
                  ].map(([icon, text]) => (
                    <div className="case-benefit-item" key={text}>
                      <span><i className={icon}></i></span>
                      <p>{text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="case-donation-panel">
              <h2>{text.progress}</h2>
              <div className="case-progress-track">
                <span className="case-progress-fill" style={{ width: `${percent}%` }}>
                  {percent}%
                </span>
              </div>

              <div className="case-amount-summary">
                <div>
                  <span>{text.remaining}</span>
                  <strong>{formatAmount(caseDetail.remainingAmount)} {text.currency}</strong>
                </div>
                <div>
                  <span>{text.collected}</span>
                  <strong>{formatAmount(caseDetail.collectedAmount)} {text.currency}</strong>
                </div>
              </div>

              <div className="case-panel-divider"></div>

              <label className="case-amount-title" htmlFor="case-donation-amount">{text.choose}</label>
              <div className="case-donate-box critical-detail-donate">
                <div className="case-donate-input-wrap">
                  <span>{text.currency}</span>
                  <input id="case-donation-amount" type="number" defaultValue="100" min="1" />
                </div>
                <button
                  type="button"
                  className="openDonation"
                  data-donation-id={caseDetail.id}
                  data-catid={caseDetail.catid || 0}
                  data-subcatid="0"
                  data-projectid={caseDetail.projectid || caseDetail.id || 0}
                  data-donation-title={caseDetail.title}
                  data-donation-type="critical-case"
                  data-donation-type-label="Critical Cases"
                  data-case-no={caseDetail.caseNo || ""}
                  data-category-title={caseDetail.categoryTitle || ""}
                  data-donation-amount="100"
                >
                  <strong><i className="fa-solid fa-hands-holding-child"></i> {text.donate}</strong>
                  <small><i className="fa-solid fa-lock"></i> {text.secure}</small>
                </button>
              </div>

              <div className="case-quick-divider">
                <span>{text.quick}</span>
              </div>

              <SmsDonationOptions
                language={language}
                currency={text.currency}
                codeLabel={text.code}
                iconClass="fa-solid fa-hands-holding-circle"
                showTitle={false}
              />

              <div className="case-secure-banner">
                <i className="fa-solid fa-shield-halved"></i>
                <p>{text.secureNote}</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
